//TextBox.jsx
import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';

import 'react-quill/dist/quill.snow.css';
import './WriterTextBox.css';
import './CSSFixedHeightCardButton.css';
import './CSSAICardButton.css';
import { Button } from 'react-bootstrap';
import FunctionsReactQuillCustomized from './FunctionsReactQuillCustomized'; // Import the new component
import TextBoxAI from './TextBoxAI'; // Import the AI component

const TextBox = forwardRef(({ content, onContentChange, onSaveStyle, onFocus }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false); // false = fixed height by default

    const toggleHeight = () => {
        setIsExpanded(prev => !prev);
    };

    useEffect(() => {
        let lastClickedTextBoxElement = null;

        const applyVisibility = () => {
            // Hide all buttons
            document.querySelectorAll('#buttonContainerSaveStyleAndClearStyle').forEach(btn => {
                btn.style.visibility = 'hidden';
            });

            // Show the button in the last clicked TextBox, if it still exists
            if (lastClickedTextBoxElement) {
                const button = lastClickedTextBoxElement.querySelector('#buttonContainerSaveStyleAndClearStyle');
                if (button) {
                    button.style.visibility = 'visible';
                }
            } else {
                // Fallback: show button in first TextBox (on page load)
                const firstTextBox = document.querySelector('.textBoxPlacement #buttonContainerSaveStyleAndClearStyle');
                if (firstTextBox) {
                    firstTextBox.style.visibility = 'visible';
                    lastClickedTextBoxElement = firstTextBox.closest('.textBoxPlacement');
                }
            }
        };

        const handleClick = (e) => {
            // Find the closest TextBox container
            const textBoxContainer = e.target.closest('.textBoxPlacement');

            if (textBoxContainer) {
                lastClickedTextBoxElement = textBoxContainer;
                applyVisibility();
            }
        };

        // MutationObserver to reapply visibility when DOM changes
        const observer = new MutationObserver(() => {
            applyVisibility();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        applyVisibility();
        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
            observer.disconnect();
        };
    }, []);

    const quillRef = useRef(null);

    useImperativeHandle(ref, () => ({
        getEditor: () => quillRef.current?.getEditor()
    }));

    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (editor) {
            editor.format('size', '16px');
            editor.format('line-height', '1.5');
        }
    }, []);

    const handleSaveStyle = () => {
        const selection = quillRef.current?.getEditor().getSelection();
        if (selection && selection.length > 0) {
            const format = quillRef.current?.getEditor().getFormat(selection);
            if (onSaveStyle) {
                onSaveStyle(format);
            }
        } else {
            console.log('No text selected. Style not saved.');
        }
    };

    const handleClearStyle = () => {
        const editor = quillRef.current?.getEditor();
        const selection = editor?.getSelection();

        if (editor && selection && selection.length > 0) {
            editor.removeFormat(selection.index, selection.length);
            console.log('Cleared formatting from selected text.');
        } else {
            console.log('No text selected. Nothing to clear.');
        }
    };

    const findButtonHideViewsTagsFilters = () => {
        const button = document.querySelector('button#buttonHideViewsTagsFilters');
        if (button) {
            button.click();
        } else {
            console.warn('buttonHideViewsTagsFilters not found in DOM');
        }
    };

    const findButtonSavedStylesShow = () => {
        const button = document.querySelector('button#buttonSavedStylesShow');
        if (button) {
            button.click();
        } else {
            console.warn('buttonSavedStylesShow not found in DOM');
        }
    };

    return (
        <div className="textBoxPlacement">
            <div
                id="buttonContainerSaveStyleAndClearStyle"
                className="buttonContainerSaveStyleAndClearStyle col-7 col-md-12"
            >
                <Button
                    id="buttonSaveStyleOfSelectedText"
                    className="buttonSaveStyleOfSelectedText"
                    variant="primary"
                    size="sm"
                    onClick={() => {
                        handleSaveStyle();
                        findButtonHideViewsTagsFilters();
                        findButtonSavedStylesShow();
                    }}
                >
                    <div
                        id="buttonSaveStyleOfSelectedTextBox"
                        className="buttonSaveStyleOfSelectedTextBox">

                        <div
                            id="buttonSaveStyleOfSelectedTextBoxImage"
                            className="buttonSaveStyleOfSelectedTextBoxImage"
                        >
                            <img
                                id="buttonSaveStyleOfSelectedTextBoxImageImg"
                                src="/SVGicons/savestylesindent-black.svg"
                                alt="SAVE Style of Selected Text"
                            />
                        </div>
                        <div
                            id="buttonSaveStyleOfSelectedTextBoxText"
                            className="buttonSaveStyleOfSelectedTextBoxText"
                        >
                            <div className="buttonSaveStyleOfSelectedTextBoxTextDesktop">SAVE Style of Selected Text</div>
                            <div className="buttonSaveStyleOfSelectedTextBoxTextMobile">SAVE Style</div>
                        </div>
                    </div>
                </Button>
                <Button
                    id="buttonClearStyleOfSelectedText"
                    className="buttonClearStyleOfSelectedText"
                    variant="secondary"
                    size="sm"
                    onClick={handleClearStyle}
                >
                    <div
                        id="buttonClearStyleOfSelectedTextBox"
                        className="buttonClearStyleOfSelectedTextBox">

                        <div
                            id="buttonClearStyleOfSelectedTextBoxImage"
                            className="buttonClearStyleOfSelectedTextBoxImage"
                        >
                            <img
                                id="buttonClearStyleOfSelectedTextBoxImageImg"
                                src="/SVGicons/clearstylesindent-black.svg"
                                alt="CLEAR Style of Selected Text"

                            />
                        </div>
                        <div
                            id="buttonClearStyleOfSelectedTextBoxText"
                            className="buttonClearStyleOfSelectedTextBoxText"
                        >
                            <div className="buttonClearStyleOfSelectedTextBoxTextDesktop">CLEAR Style from Selected Text</div>
                            <div className="buttonClearStyleOfSelectedTextBoxTextMobile">CLEAR Style</div>
                        </div>
                    </div>
                </Button>
            </div>

            <div>
                <div
                    id="reactQuillForTextBoxContainer"
                    className={`reactQuillForTextBoxContainer ${isExpanded ? 'auto-height' : 'fixed-height'}`}
                >
                    <FunctionsReactQuillCustomized
                        ref={quillRef}
                        content={content}
                        onChange={onContentChange}
                        onFocus={onFocus}
                    />

                    {/* AI button positioned in upper right corner */}
                    <div className="buttonAIContainerParent">
                        <div className="buttonAIContainer">
                            <TextBoxAI onContentChange={onContentChange} quillRef={quillRef} />
                        </div>
                    </div>

                    <div className="buttonToggleFixedHeightAutoHieghtContainerParent">
                        <div className="buttonToggleFixedHeightAutoHieghtContainer">
                            <Button
                                id="buttonToggleFixedHeightAutoHieght"
                                onClick={toggleHeight}
                                size="sm"
                                className="buttonToggleFixedHeightAutoHieght"
                            >
                                <div
                                    id="buttonToggleFixedHeightAutoHieghtBox"
                                    className="buttonToggleFixedHeightAutoHieghtBox"
                                >
                                    <div
                                        id="buttonToggleFixedHeightAutoHieghtBoxImage"
                                        className="buttonToggleFixedHeightAutoHieghtBoxImage"
                                    >
                                        <img
                                            id="buttonToggleFixedHeightAutoHieghtBoxImageImg"
                                            src={
                                                isExpanded
                                                    ? '/SVGicons/height-fixed-black.svg'
                                                    : '/SVGicons/height-auto-black.svg'
                                            }
                                            alt={isExpanded ? 'Fixed Height' : 'Auto Height'}
                                        />
                                    </div>
                                    <div
                                        id="buttonToggleFixedHeightAutoHieghtBoxText"
                                        className="buttonToggleFixedHeightAutoHieghtBoxText"
                                    >
                                        <span className="button-text">
                                            {isExpanded ? 'Set Card to Fixed Height' : 'Set Card to Auto Height'}
                                        </span>
                                    </div>
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default TextBox;